'use client';

import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import {
  batteryLevel,
  batteryPercent,
  exceptionsFor,
  formatAge,
  linkFreshness,
  type DroneException,
} from '@/lib/drone-telemetry';
import type { LiveDrone } from '@/hooks/useWebSocket';
import type { AlarmEntry } from '@/components/dashboard/alarmTypes';
import { CHIP_STATE_LABEL, type ChipStateKey } from '@/lib/copy';

/**
 * Level two: which aircraft is selected, and whether anything is wrong with it.
 *
 * This replaces DroneStateCard, which was 252 px wide and 295-396 px tall and
 * anchored to the marker — so it covered between 44% and 60% of the map's
 * height, and it covered it at the exact point the operator was watching. The
 * three rows that made it tall (companion, mission, exceptions) all appear
 * together in one situation: a companion aircraft flying a mission with a
 * condition open. That is precisely when the airspace ahead matters most.
 *
 * The rule this file follows, and the card did not:
 *
 *     the marker gets identity · the panel gets data · the bar gets verbs
 *
 * So identity and the two numbers that decide whether the flight continues stay
 * here. Link percent, satellite count and ground speed moved to DroneWorkspace's
 * FactGrid, which is one click away and already open whenever DETAILS is.
 *
 * Positioning is NOT this component's job. DroneMap owns the marker-anchored
 * wrapper and transforms it imperatively on every map `move`; this renders
 * inside that wrapper at its natural size. `anchoredCardPosition` measures the
 * overlay live, so shrinking the content is all that was needed to re-anchor it.
 */

type ChipState = ChipStateKey;

/**
 * Flight state is a word first and a colour second.
 *
 * An earlier form of this chip printed the word `sr-only` and left the coloured
 * left stripe as the only thing on screen, on the reasoning that visually-hidden
 * text satisfies SC 1.4.1. It does not: 1.4.1 is about what a *sighted* operator
 * can perceive, and screen-reader-only text does nothing for the ~8% of men with
 * a red-green deficiency, to whom the green "Flying" stripe and the amber "Link weak"
 * stripe are the same stripe. Nothing else on the display covers for it either —
 * droneMapLayers appends "· LOST" to the marker label only at stale_level 2, so
 * "Link weak" is wordless everywhere on the map. So the word renders visibly, in
 * the state's own text colour, and the stripe is the redundant second carrier.
 *
 * `showAge` is true for "Link weak" alone, and the asymmetry is the point.
 *
 * A stale link is emitted at warning severity, and `exceptionsFor` sorts
 * severity before kind — so a critical battery (or a critical alert) outranks
 * it and pushes "Telemetry old, 47s" off the pill, which renders
 * `exceptions[0]` only. A 12% aircraft silent for 40 seconds would then read
 * "EAGLE-01 LINK WEAK | 12% | 103m": the word says the link is bad, nothing says
 * the battery figure beside it is 40 seconds old, and the operator makes the
 * return-to-home call on it anyway. So "Link weak" carries the age itself.
 *
 * The cost is that when "Telemetry old" *is* the top exception — the common
 * lone-stale case, not an edge case — the age appears twice, here and in the
 * pill. That is accepted, and is what DroneStateCard did: header "Link weak,
 * 47s" over a strip reading "Telemetry old, 47s". Duplication is the price
 * of the guarantee, not an argument against it.
 *
 * "Link lost" needs no guarantee, so it does not pay that price. It is emitted at
 * *critical* severity, and KIND_RANK puts `link` ahead of `battery`, so a lost
 * link is always `exceptions[0]` and the pill always reads "NO TELEMETRY, 2m".
 * An age here would duplicate unconditionally — three copies, counting the
 * marker label's own "· LOST" — buying nothing back.
 */
const STATE_STYLE: Record<ChipState, { label: string; text: string; stripe: string; showAge: boolean }> = {
  flying: { label: CHIP_STATE_LABEL.flying, text: 'text-status-green-fg', stripe: 'border-l-status-green-marker', showAge: false },
  // Two words for "not flying", because since #338 the platform knows two different things by it.
  //
  // `grounded` is the aircraft SAYING it is down (`is_flying === false`) - a fact, and the common
  // case for the DJI fleet, which has the best data of any source. Watering that down to a reading
  // would make the display vaguer exactly where it is most certain.
  //
  // `below_bar` is every other source, where "not flying" is still only `altitude <= 2 m` and a
  // MAVLink drone genuinely hovering at 1.9 m lands here. "On the ground" there is a positive claim
  // the reading cannot support, and asserting it to an operator watching that aircraft hover is
  // the same misread #338 was filed for, one fleet over. Same colour either way: the operator's
  // decision is identical, only our confidence differs.
  grounded: { label: CHIP_STATE_LABEL.grounded, text: 'text-status-blue-fg', stripe: 'border-l-status-blue-marker', showAge: false },
  below_bar: { label: CHIP_STATE_LABEL.below_bar, text: 'text-status-blue-fg', stripe: 'border-l-status-blue-marker', showAge: false },
  stale: { label: CHIP_STATE_LABEL.stale, text: 'text-status-amber-fg', stripe: 'border-l-status-amber-marker', showAge: true },
  lost: { label: CHIP_STATE_LABEL.lost, text: 'text-status-gray-fg', stripe: 'border-l-status-gray-marker', showAge: false },
};

function stateOf(drone: LiveDrone, now: number): ChipState {
  const freshness = linkFreshness(drone.last_seen_at, now);
  if (freshness === 'lost') return 'lost';
  if (freshness === 'stale') return 'stale';
  if (drone.status === 'flying') return 'flying';
  // `=== false` and not falsiness: absent means the source cannot answer, which is most of them,
  // and only an explicit "no" from the aircraft earns the certain word. See STATE_STYLE.
  return drone.is_flying === false ? 'grounded' : 'below_bar';
}

const BATTERY_TEXT: Record<'ok' | 'warn' | 'critical', string> = {
  ok: 'text-status-green-fg',
  warn: 'text-status-amber-fg',
  critical: 'text-status-red-fg',
};

export interface DroneSelectionChipProps {
  drone: LiveDrone;
  /** Feeds the exception pill. Same source the card read. */
  alerts?: AlarmEntry[];
  /** Deselect. */
  onClose: () => void;
  /** Opens the details sheet, where the conditions beyond the first one live. */
  onOpenDetails: () => void;
  /** Freezes the internal 1 Hz clock; for deterministic tests. */
  now?: number;
}

export function DroneSelectionChip({
  drone,
  alerts = [],
  onClose,
  onOpenDetails,
  now: nowOverride,
}: DroneSelectionChipProps) {
  // An aircraft going quiet is exactly when the clock has to keep moving: the
  // staleness that turns this chip's state word to "Link weak", and the age printed
  // beside it, are functions of elapsed time, not of an arriving message. Same
  // 1 Hz ticker the card used.
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    if (nowOverride !== undefined) return;
    const id = window.setInterval(() => setTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [nowOverride]);
  const now = nowOverride ?? tick;

  const callsign = drone.callsign?.trim() || drone.drone_id.slice(-6).toUpperCase();
  const state = STATE_STYLE[stateOf(drone, now)];
  const battery = batteryPercent(drone);
  const exceptions = exceptionsFor(drone, alerts, now);

  return (
    <div className="flex flex-col items-start gap-1">
      <div
        /*
          `bg-popover`, opaque. It was `bg-popover/95`, and the 5% of basemap
          coming through is not free: the app theme and the basemap style toggle
          independently, so the dark theme's `--muted-foreground` —
          which paints the altitude, the "Link weak" age and the "N/A" battery —
          composites against the pale CARTO voyager at 4.32:1 at 11px. Under
          WCAG 1.4.3's 4.5:1, on one of the three readings this chip exists to
          carry. DroneStateCard sat at `/97` and cleared it at 4.54:1; opaque is
          4.81:1 on any basemap. `backdrop-blur` goes with it — there is nothing
          left to blur.
        */
        className={cn(
          'flex items-center gap-2 whitespace-nowrap rounded-md border border-subtle border-l-[3px]',
          'bg-popover py-1 pl-2 pr-1 text-[11px] shadow-lg',
          state.stripe,
        )}
        data-state={state.label}
        role="status"
        aria-live="off"
        aria-label={`${callsign} selected`}
      >
        {/* `title` for the same reason the exception pill carries one: 13ch
            truncates real callsigns, and two aircraft on the same site often
            share a prefix. `aria-label` above already carries it in full. */}
        <span
          title={callsign}
          className="callsign text-[11px] max-w-[13ch] truncate text-foreground"
        >
          {callsign}
        </span>
        <span className={cn('shrink-0 uppercase tracking-wider', state.text)}>{state.label}</span>
        {state.showAge && (
          <span className="-ml-1 shrink-0 font-mono tabular-nums text-muted-foreground">
            {`· ${formatAge(drone.last_seen_at, now)}`}
          </span>
        )}

        <span className="h-3 w-px shrink-0 bg-border" aria-hidden />
        {battery === null ? (
          // RemoteID broadcasts no battery state — "N/A", never a misleading 0%.
          <span className="text-muted-foreground">N/A</span>
        ) : (
          <span className={cn('font-mono tabular-nums', BATTERY_TEXT[batteryLevel(battery)])}>{battery}%</span>
        )}

        <span className="h-3 w-px shrink-0 bg-border" aria-hidden />
        <span className="font-mono tabular-nums text-muted-foreground">{Math.round(drone.altitude ?? 0)}m</span>

        <button
          type="button"
          onClick={onClose}
          aria-label={`Deselect ${callsign}`}
          // 24 px hit area on a 13 px glyph (SC 2.5.8). The chip sets 11 px type,
          // so the target has to be built from padding, not from the glyph.
          className="ml-0.5 flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <span aria-hidden className="text-[13px] leading-none">✕</span>
        </button>
      </div>

      {exceptions.length > 0 && (
        <ExceptionPill exceptions={exceptions} onOpenDetails={onOpenDetails} />
      )}
    </div>
  );
}

/**
 * Border and text only — the severity wash is EXCEPTION_WASH, and it is painted
 * as its own layer over an opaque base rather than straight onto the map.
 *
 * These classes used to carry `bg-status-red/15` directly. Inside DroneStateCard
 * that was safe: the card's surface was `bg-popover/97`, so a 15% tint always
 * composited over a known, theme-matched base. Out on the map it is not. The
 * app theme and the basemap style are toggled independently — DroneMap persists
 * `mapStyle` separately from the theme — and `STYLES.bright` is a pale CARTO
 * voyager. Amber text over a 15% amber tint over pale cream lands near 1.8:1 at
 * 10.5px, so the one element on the display whose whole job is to be noticed
 * becomes the least readable thing on it.
 *
 * The opaque base matters twice over, because the wash is not one value. At
 * rest it is 15% and a `bg-popover/95` base still cleared AA — thinly, at
 * 4.60:1 for critical over a bright basemap. On HOVER it goes to 25% and that
 * same base fell to 4.01:1: the pill got HARDER to read the moment the operator
 * reached for it, and the test below pinned only the resting layer, so it
 * passed. Opaque holds both — 5.07:1 at rest, 4.52:1 hovered.
 */
const EXCEPTION_TONE: Record<DroneException['severity'], string> = {
  critical: 'border-status-red/40 text-status-red-fg',
  warning: 'border-status-amber/40 text-status-amber-fg',
};

/** The severity wash, painted between the opaque base and the text. */
const EXCEPTION_WASH: Record<DroneException['severity'], string> = {
  critical: 'bg-status-red/15 group-hover:bg-status-red/25',
  warning: 'bg-status-amber/15 group-hover:bg-status-amber/25',
};

const EXCEPTION_GLYPH: Record<DroneException['severity'], string> = {
  critical: '▼',
  warning: '▲',
};

/**
 * Master caution: one row, however bad the flight is going.
 *
 * The card expanded this list in place, which put its height back under the
 * control of how much was wrong. On the map that is not affordable, so the
 * remainder is a count that opens the sheet — the operator still learns *that*
 * there is more, and gets the full list somewhere it costs no airspace to read.
 *
 * `exceptionsFor` has already collapsed re-fires of the same rule, so `+N`
 * counts distinct conditions. A repeat count would put the noise back.
 */
function ExceptionPill({
  exceptions,
  onOpenDetails,
}: {
  exceptions: DroneException[];
  onOpenDetails: () => void;
}) {
  const [top, ...rest] = exceptions;

  return (
    <button
      type="button"
      onClick={onOpenDetails}
      aria-label={
        rest.length === 0
          ? `${top.label}. Open details.`
          : `${top.label}, and ${rest.length} further condition${rest.length === 1 ? '' : 's'}. Open details.`
      }
      className={cn(
        // `isolate` keeps the -z-10 wash inside the pill's own stacking context
        // instead of letting it fall behind the map canvas.
        'group relative isolate flex max-w-[280px] items-center gap-2 overflow-hidden rounded-md border px-2 py-1',
        'bg-popover text-[10.5px] tracking-[0.05em] shadow-lg',
        'outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring',
        EXCEPTION_TONE[top.severity],
      )}
    >
      <span aria-hidden className={cn('absolute inset-0 -z-10', EXCEPTION_WASH[top.severity])} />
      <span aria-hidden className="shrink-0">{EXCEPTION_GLYPH[top.severity]}</span>
      {/* The label truncates at 280px and real ones overrun it — a proximity
          warning names two aircraft and a separation. `title` keeps the full
          text reachable on hover without growing the pill. */}
      <span className="min-w-0 truncate" title={top.label}>{top.label}</span>
      {rest.length > 0 && (
        <span aria-hidden className="shrink-0 tabular-nums opacity-70">+{rest.length}</span>
      )}
    </button>
  );
}
